import { useMemo } from 'react';
import type { IndividualPaymentParcelDetailViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentParcelsParcelid } from '@/shared/api/generated';
import dayjs from '@/shared/lib/dayjs';
import type { ParcelFieldName } from '../types/parcelEdit';
import { readStatus } from '../utils/planWriteError';
import type { ParcelEditFormValues } from './useParcelEditFormSchema';

/** Semeadura do formulário de edição de parcela. */
export interface ParcelEditSeed {
  /** A parcela escolhida, para o cabeçalho e os campos somente leitura. */
  readonly parcel: IndividualPaymentParcelDetailViewModel | undefined;
  /** Valores iniciais do formulário. */
  readonly values: Partial<ParcelEditFormValues>;
  /**
   * Campos que esta fonte não cobre; ficam desabilitados e fora do payload.
   *
   * **Vazio com o endpoint de detalhe**, que devolve exatamente os campos que o
   * `PUT` aceita. A lista continua existindo porque é o mecanismo que protege o
   * `PUT` full-replace: se uma fonte futura voltar a ser parcial, é aqui que isso
   * se declara.
   */
  readonly unseededFields: readonly ParcelFieldName[];
  /**
   * A fonte cobre todos os campos do `IndividualPaymentParcelSaveViewModel`?
   *
   * `true` quando o detalhe chegou — é o único valor que libera a escrita da tela.
   * Enquanto carrega ou se a leitura falha, continua `false`: sem os valores
   * atuais, um `PUT` full-replace apagaria o que não foi semeado.
   */
  readonly isComplete: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** O endpoint respondeu 404 — a parcela não existe. */
  readonly isNotFound: boolean;
  readonly refetch: () => void;
}

/**
 * `YYYY-MM-DD` — o formato que o `DatePicker` guarda no formulário.
 *
 * **Lido em UTC de propósito.** O backend manda `date-time` à meia-noite Z
 * (`2022-08-26T00:00:00Z`); interpretar isso no fuso local, que aqui é negativo,
 * cai no dia anterior e o campo exibiria 25/08. Com o Salvar liberado esse desvio
 * deixaria de ser cosmético: o `PUT` é full-replace, então uma gravação sem
 * nenhuma edição regravaria o vencimento um dia antes.
 */
const toDateInput = (value: string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

/**
 * Nenhum campo fica sem semeadura: o detalhe cobre o payload inteiro.
 *
 * Constante nomeada, e não um literal solto no retorno, para que a referência
 * seja estável entre renders — `values` e esta lista alimentam o `reset` do
 * formulário na página.
 */
const ALL_FIELDS_SEEDED: readonly ParcelFieldName[] = [];

/**
 * Valores iniciais do formulário de edição de parcela.
 *
 * A fonte é `GET /api/v1/individualpayment/parcels/{parcelId}`, que devolve — nas
 * palavras do próprio endpoint — "a parcela com exatamente os campos que o PUT
 * aceita", mais os textos traduzidos e o contexto somente leitura. Isso é o que
 * libera a escrita da tela: como o `PUT` substitui o registro, editar só é seguro
 * quando o formulário conhece o valor atual de cada campo.
 *
 * **Era a grid do contrato**, que é um ViewModel de tabela e não trazia `fine`,
 * `message`, os dois descontos, `invoiceIssueDate` nem `fractionOfParcel` — os seis
 * de `UNSEEDED_FROM_GRID_FIELDS`. Com o detalhe, `unseededFields` chega vazio e
 * todos os campos viajam no corpo.
 *
 * `typeId` agora é semeado de verdade: o detalhe devolve o id, não só o rótulo
 * (`typeText`). Antes ficava sem valor, o que reprovaria a validação do schema —
 * irrelevante enquanto o Salvar estava travado, mas bloqueante agora.
 *
 * @param parcelId - ID da parcela a editar; `null` desabilita a query.
 * @returns Parcela, valores iniciais, campos sem semeadura e estados de carga.
 *
 * @example
 * ```tsx
 * const { values, isComplete } = useParcelEditSeed(42);
 * ```
 */
export function useParcelEditSeed(parcelId: number | null): ParcelEditSeed {
  const { data, isLoading, isError, error, refetch } = useGetApiV1IndividualpaymentParcelsParcelid(
    parcelId ?? 0,
    undefined,
    { query: { enabled: parcelId !== null } },
  );

  const parcel = data;

  const values = useMemo<Partial<ParcelEditFormValues>>(() => {
    if (parcel === undefined) return {};

    // `exactOptionalPropertyTypes` está ligado: uma chave presente com `undefined`
    // não é a mesma coisa que chave ausente. Os campos numéricos opcionais são
    // omitidos quando o backend não devolve valor, em vez de semeados com
    // `undefined` — o formulário exibe vazio e o payload não os envia.
    const num = <K extends string>(key: K, value: number | null | undefined) =>
      value === null || value === undefined ? {} : { [key]: value };

    return {
      ...num('typeId', parcel.typeId),
      ...num('numberParcel', parcel.numberParcel),
      ...num('total', parcel.total),
      ...num('paymentMethod', parcel.paymentMethod),
      ...num('serial', parcel.serial),
      ...num('discountPunctualPercentage', parcel.discountPunctualPercentage),
      ...num('totalDiscount', parcel.totalDiscount),
      ...num('retroactiveDiscount', parcel.retroactiveDiscount),
      ...num('fine', parcel.fine),
      ...num('fractionOfParcel', parcel.fractionOfParcel),
      expirationDate: toDateInput(parcel.expirationDate),
      invoiceIssueDate: toDateInput(parcel.invoiceIssueDate),
      observation: parcel.observation ?? '',
      message: parcel.message ?? '',
      invoiceNumber: parcel.invoiceNumber ?? '',
      // As duas flags são comandos do operador, não estado da parcela: começam
      // desligadas em toda abertura.
      removePunctualDiscount: false,
      resetExtendedExpiration: false,
    };
  }, [parcel]);

  // O 404 é resposta de negócio, não falha de leitura: sai de `isError` para que a
  // página distinga "parcela inexistente" de "não foi possível ler", que pedem
  // telas diferentes — uma sem ação de repetir, a outra com.
  const isNotFound = readStatus(error) === 404;

  return {
    parcel,
    values,
    unseededFields: ALL_FIELDS_SEEDED,
    isComplete: parcel !== undefined,
    isLoading: parcelId !== null && isLoading,
    isError: isError && !isNotFound,
    isNotFound,
    refetch: () => void refetch(),
  } as const;
}
